/**
 * RepairDetailPage — Landlord Portal
 *
 * Route: /repairs/:id
 *
 * Layout follows the design mockup (Repair Detail — Desktop/Mobile boards): escalation /
 * paused banners on top, ONE summary card (title, status chips, inline Agent Policy switches +
 * Pause, description, unit/tenant/vendor/schedule footer), and an inbox-style Conversation card
 * (RA-CONVERSATION-THREADS: thread list + selected thread + docked composer) — thread list
 * collapses to chips on mobile. Manual close button preserved.
 */

import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  getRepair,
  getRepairTimeline,
  getRepairParties,
  getThreadReads,
  markThreadRead,
  closeRepair,
  listRepairAttachments,
  type RepairRequest,
  type TimelineItem,
  type RepairParty,
} from '../api/repairs.api';
import type { RepairThreadRead } from '@resida/api-types/repair';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { CaseAgentPolicySection } from '../components/agent/CaseAgentPolicySection';
import { CaseEscalationSection } from '../components/agent/CaseEscalationSection';
import { RepairAgentStateBadge } from '../components/repair/RepairAgentStateBadge';
import { RepairPhotoGallery } from '../components/repair/RepairPhotoGallery';
import { MessageMarkdown } from '../components/repair/MessageMarkdown';
import { ThreadUnreadBadge } from '../components/repair/ThreadUnreadBadge';
import { useCaseActivityRefresh } from '../hooks/useCaseActivityRefresh';
import { RepairComposer, partyOptionValue, partyOptionLabel } from '../components/repair/RepairComposer';
import { useBillingFeatures } from '../hooks/useBilling';
import { useMarkSeen } from '../hooks/useAwareness';
import { useIsProductionEnvironment } from '../hooks/useSystemEnvironment';
import { useUnitTimezone } from '../hooks/useUnitTimezone';
import { usePropertyTimezone } from '../hooks/usePropertyTimezone';
import { PropertyTimezoneProvider } from '../contexts/PropertyTimezoneContext';
import { deriveTimezone, formatTimestamp } from '../utils/date';
import { ActivityRow } from '../components/repair/ActivityRow';
import {
  asIsoDateTimeString,
  eventThreadKey,
  formatUnitAddress,
  isUnreadCandidate,
  REPAIR_MESSAGE_EVENT_TYPES,
  CASE_THREAD_UNREAD_EPOCH,
} from '@resida/shared';
import { useQuery } from '@tanstack/react-query';
import { caseFeedbackQueryKey, listCaseFeedback, reportsByEventId, type LandlordFeedbackView } from '../api/agent-feedback.api';
import { AgentFeedbackReportsList } from '../components/agent/AgentFeedbackReportsList';
import { ImproveAgentButton, MessageFeedbackControls } from '../components/agent/improvement/ImprovementEntryButton';
import { isImprovableAgentMessage } from '../components/agent/improvement/improvableMessage';

// Status chips use the semantic tint pattern (bg-<tone>/15 text-<tone>); the tokens carry the
// light/dark values, so no palette or `dark:` classes belong here.
const STATUS_CONFIG = {
  open: { label: 'Open', className: 'bg-success/15 text-success hover:bg-success/25' },
  in_progress: { label: 'In Progress', className: 'bg-warning/15 text-warning hover:bg-warning/25' },
  closed: { label: 'Closed', className: 'bg-muted text-foreground hover:bg-accent' },
} as const;

// Sender identity per actorKind: avatar letter + avatar colours, the label pill colours, and
// the bubble background — matching the design mockup's thread palette (Agent = amber,
// tenant = blue, vendor = violet, landlord ("You") = neutral on a pale-blue bubble). The
// pills are informational and never interactive, so each repeats its own background under
//`hover:` — Badge's`secondary` variant otherwise applies`hover:bg-secondary/80` (a
// higher-specificity rule) and flips the pill to the grey secondary colour under the cursor.
const SENDER_STYLES: Record<string, { letter: string; avatar: string; pill: string; bubble: string }> = {
  agent: { letter: 'A', avatar: 'bg-warning/15 text-warning', pill: 'bg-warning/15 text-warning hover:bg-warning/25', bubble: 'bg-warning/10' },
  router: { letter: 'A', avatar: 'bg-warning/15 text-warning', pill: 'bg-warning/15 text-warning hover:bg-warning/25', bubble: 'bg-warning/10' },
  tenant: { letter: 'T', avatar: 'bg-info/15 text-info', pill: 'bg-info/15 text-info hover:bg-info/25', bubble: 'bg-muted/60' },
  vendor: { letter: 'V', avatar: 'bg-primary/15 text-primary', pill: 'bg-primary/15 text-primary hover:bg-primary/25', bubble: 'bg-muted/60' },
  landlord: { letter: 'Y', avatar: 'bg-muted text-foreground', pill: 'bg-muted text-foreground hover:bg-accent', bubble: 'bg-info/10' },
  system: { letter: 'S', avatar: 'bg-muted text-foreground', pill: 'bg-muted text-foreground hover:bg-accent', bubble: 'bg-muted/60' },
  admin: { letter: 'S', avatar: 'bg-destructive/15 text-destructive', pill: 'bg-destructive/15 text-destructive hover:bg-destructive/25', bubble: 'bg-muted/60' },
};
const DEFAULT_SENDER_STYLE = SENDER_STYLES.system!;

/**
 * Event types that render as message bubbles (chat-style). All other event
 * types render as activity rows (system-event style).
 */
const MESSAGE_EVENT_TYPES = new Set<string>(REPAIR_MESSAGE_EVENT_TYPES);

/** RA-TIMELINE-LABELS — a friendlier sender label naming who a bubble actually went
 *  to/came from, instead of the generic actorKind badge. Returns null for every other
 *  bubble (tenant/vendor-authored messages keep the plain actorKind badge). */
function messageSenderLabel(item: TimelineItem): string | null {
  if (item.actorKind === 'agent') {
    if (item.eventType === 'case.comment_added') return 'Agent → You';
    if (item.eventType === 'case.message_sent') {
      const recipientType = item.payload?.recipient_type;
      if (recipientType === 'tenant') return 'Agent → Tenant';
      if (recipientType === 'vendor') return 'Agent → Vendor';
    }
    return null;
  }
  if (item.actorKind !== 'landlord') return null;
  if (item.eventType === 'case.comment_added') return 'You → Agent (note)';
  if (item.eventType === 'case.message_sent') {
    const recipientType = item.payload?.recipient_type;
    if (recipientType === 'tenant') return 'You → Tenant';
    if (recipientType === 'vendor') return 'You → Vendor';
  }
  return null;
}

function RepairStatusBadge({
  status,
  vendorCompletedAt,
}: {
  status: string;
  vendorCompletedAt: string | null;
}) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? {
    label: status,
    className: 'bg-muted text-foreground',
  };
  return (
    <div>
      <Badge
        data-testid="repair-detail-status-badge"
        className={`rounded-full px-2.5 font-bold ${cfg.className}`}
        variant="secondary"
      >
        {cfg.label}
      </Badge>
      {status === 'in_progress' && vendorCompletedAt && (
        <div
          data-testid="awaiting-confirmation-indicator"
          className="text-xs text-warning mt-0.5 font-medium"
        >
          Awaiting Confirmation
        </div>
      )}
    </div>
  );
}

function SenderAvatar({ actorKind, size = 'md' }: { actorKind: string; size?: 'sm' | 'md' }) {
  const style = SENDER_STYLES[actorKind] ?? DEFAULT_SENDER_STYLE;
  return (
    <span
      aria-hidden="true"
      className={`flex flex-none items-center justify-center rounded-full font-extrabold ${style.avatar} ${
        size === 'sm' ? 'h-[26px] w-[26px] text-[11px] md:h-8 md:w-8 md:text-[13px]' : 'h-8 w-8 text-[13px]'
      }`}
    >
      {style.letter}
    </span>
  );
}

/** Agent-feedback wiring threaded into agent bubbles (RA-IMP-ENTRY, RA-FB-REPORTED). */
interface BubbleFeedback {
  caseId: string;
  /** CaseEvents a live report is about: drives the "Reported" chip. */
  reportsByEvent: Map<string, LandlordFeedbackView>;
  /** The unit the repair is for, e.g. "12 Elm St — Unit 2"; names the anchor in the panel header. */
  unitLabel: string | null;
}

// Exported only for the component test of the improvement entry (IMP-BR-4, BR-AGENT-FB-1).
export function MessageBubble({ item, feedback }: { item: TimelineItem; feedback?: BubbleFeedback }) {
  const propertyTimezone = usePropertyTimezone();
  const actorKind = item.actorKind;
  const style = SENDER_STYLES[actorKind] ?? DEFAULT_SENDER_STYLE;
  const content =
    typeof item.payload?.content === 'string' ? item.payload.content : '';
  const senderLabel = messageSenderLabel(item);
  // The landlord's own messages sit on the right (chat convention); everyone else on the left.
  const mine = actorKind === 'landlord';
  const isAgent = actorKind === 'agent' || actorKind === 'router';
  const canImprove = isImprovableAgentMessage(item.eventType, actorKind);
  return (
    <div
      data-testid={`timeline-message-${item.id}`}
      className={`flex max-w-[640px] gap-2.5 ${mine ? 'ml-auto flex-row-reverse' : ''}`}
    >
      <SenderAvatar actorKind={actorKind} size="sm" />
      <div className={`min-w-0 rounded-xl px-3 py-2.5 md:px-4 md:py-3 ${style.bubble}`}>
        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge className={`rounded-full px-2 py-0.5 text-[11px] md:text-xs ${style.pill}`} variant="secondary">
            {senderLabel ?? actorKind}
          </Badge>
          <span className="text-[11px] text-muted-foreground md:text-xs">
            {formatTimestamp(asIsoDateTimeString(item.createdAt), propertyTimezone)}
          </span>
        </div>
        {isAgent ? (
          <MessageMarkdown content={content} />
        ) : (
          <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{content}</p>
        )}
        {canImprove && feedback && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <MessageFeedbackControls
              eventId={item.id}
              report={feedback.reportsByEvent.get(item.id)}
              request={{
                start: { entry: 'message', anchor: { kind: 'message', caseEventId: item.id }, caseId: feedback.caseId },
                agentKind: 'repair',
                subtitle: feedback.unitLabel ? `About a message on ${feedback.unitLabel}` : 'About a message',
                anchorCard: {
                  label: 'The agent sent',
                  text: content,
                  at: formatTimestamp(asIsoDateTimeString(item.createdAt), propertyTimezone),
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface ThreadOption {
  key: string;
  /** Full accessible name (partyOptionLabel) — kept as the tab's aria-label. */
  label: string;
  disabled: boolean;
  kind: 'agent' | 'tenant' | 'vendor';
  /** Short display name shown in the thread list and as the thread panel title's lead. */
  name: string;
  /** One-line "who sees this" caption under the name (desktop thread list). */
  roleLine: string;
  /** Thread panel title / subtitle. */
  title: string;
  subtitle: string;
  /** Last message in the thread, for the thread-list preview snippet. */
  preview: string | null;
}

/** Whether the landlord can actually see this page right now (tab visible AND window focused). */
function isTabActive(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

export function RepairDetailPage() {
  const { id } = useParams<{ id: string }>();
  // The test harness fakes inbound traffic; its API is not registered in production.
  const isProduction = useIsProductionEnvironment();
  const [repair, setRepair] = useState<RepairRequest | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // BR-16 — opening this repair marks ITS awareness items seen (referenceType 'repair_request',
  // the reference the kind-generic agent-escalation notifier writes for a repair case —
  // MF-BR-77 / RA-ESC-NOTIFY; the repair's id is the case id). Without this, the escalation
  // queue's unseen mark for a repair row never clears on open.
  const markSeen = useMarkSeen();
  useEffect(() => {
    if (id) {
      markSeen.mutate({ referenceType: 'repair_request', referenceId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { data: featuresData } = useBillingFeatures();
  const hasAgenticSupport = featuresData?.features.includes('agentic_support') ?? false;

  // Sourced by unit ID via the batched cache (issue #910) — not from repair.unit.state,
  // which requires whoever built the payload to have remembered to select it. Feeds
  // PropertyTimezoneProvider below, which CaseEscalationSection/MessageBubble/ActivityRow
  // read via usePropertyTimezone().
  const timezoneQuery = useUnitTimezone(repair?.unitId);
  const propertyTimezone = timezoneQuery.data ?? deriveTimezone('');

  // RA-CONVERSATION-THREADS — parties fetched ONCE here (lifted out of RepairComposer) and
  // shared between the thread list and the composer.
  const [parties, setParties] = useState<RepairParty[] | null>(null);
  const [partiesError, setPartiesError] = useState<string | null>(null);
  const [selectedThreadKey, setSelectedThreadKey] = useState('agent');

  // RA-UNREAD-DEF / RA-UNREAD-MARK — per-thread unread counts (null until first loaded, or when
  // the account has no thread-reads), and the "New" divider snapshot for the SELECTED thread:
  // `after` is the thread's lastReadAt captured when it became selected, BEFORE mark-read, so
  // the divider survives the mark. Re-captured the next time a thread is selected.
  const [threadReads, setThreadReads] = useState<RepairThreadRead[] | null>(null);
  const [divider, setDivider] = useState<{ key: string; after: string } | null>(null);
  const [tabActive, setTabActive] = useState(isTabActive);
  const markInflightRef = useRef(new Set<string>());

  // Close dialog
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // Bumped after a sibling mutation (party message send, manual pause, repair close) that
  // can change this case's escalation/suspension state without CaseEscalationSection's own
  // knowledge — passed through as its `refreshToken` so it reloads in place instead of only
  // updating on its own internal mutations. See CaseEscalationSection's `refreshToken` doc
  // comment.
  const [escalationRefreshToken, setEscalationRefreshToken] = useState(0);
  const bumpEscalationRefresh = useCallback(() => setEscalationRefreshToken((t) => t + 1), []);

  // Agent feedback (RA-FB-*): the report list feeds the "Reported" chips on agent bubbles.
  const { data: feedbackData } = useQuery({
    queryKey: caseFeedbackQueryKey(id ?? ''),
    queryFn: () => listCaseFeedback(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
  // Attachments (RA-ATT-LANDLORD-READ): refetched with the rest of the page on case activity so a
  // tenant's new photo appears live (RA-ATT-ACTIVITY).
  const {
    data: attachmentsData,
    isError: attachmentsFailed,
    refetch: refetchAttachments,
  } = useQuery({
    queryKey: ['repair-attachments', id ?? ''],
    queryFn: () => listRepairAttachments(id!),
    enabled: Boolean(id),
  });
  const reportsByEvent = reportsByEventId(feedbackData?.reports ?? []);

  // Focus management for close dialog
  const closeDialogRef = useRef<HTMLDivElement>(null);
  const closeDialogTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!closeDialogOpen) return;
    // Mark main content inert so screen readers cannot reach background elements
    const main = document.querySelector('main');
    main?.setAttribute('inert', '');
    // Move focus into dialog
    const dialog = closeDialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }
    // Trap Tab/Shift+Tab within the dialog, Escape to close
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setCloseDialogOpen(false);
        setCloseReason('');
        setCloseError(null);
        closeDialogTriggerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.querySelector('main')?.removeAttribute('inert');
    };
  }, [closeDialogOpen]);

  // `silent` skips the `loading` flip (and so the full-page skeleton swap that remounts
  // every child below it, including in-progress composer/section state) for an in-place
  // refresh after a mutation — e.g. CaseEscalationSection/RepairComposer/CaseAgentPolicySection's
  // onChanged/onSent/onPaused. Only the initial mount's fetch (loadRepair(), unqualified)
  // wants the loading skeleton.
  const loadRepair = useCallback(async (opts?: { silent?: boolean }) => {
    if (!id) return;
    if (!opts?.silent) setLoading(true);
    if (!opts?.silent) setError(null);
    try {
      const data = await getRepair(id);
      setRepair(data);
      if (opts?.silent) setError(null);
    } catch (err) {
      // RA-LIVE-RECONNECT — a failed background (live/resume) refetch keeps the last data and
      // shows no error state; only an explicit/initial load surfaces the error.
      if (!opts?.silent) setError(err instanceof Error ? err.message : 'Failed to load repair');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [id]);

  // Refs so the live-refresh path reads the CURRENT timeline / selection without re-creating
  // loadTimeline (which would re-run the initial-load effect).
  const timelineRef = useRef(timeline);
  timelineRef.current = timeline;
  const selectedThreadKeyRef = useRef(selectedThreadKey);
  selectedThreadKeyRef.current = selectedThreadKey;

  // `silent` (live refresh, RA-LIVE-REFRESH) skips the skeleton so the thread stays put.
  const loadTimeline = useCallback(async (opts?: { silent?: boolean }) => {
    if (!id) return;
    if (!opts?.silent) setTimelineLoading(true);
    try {
      const data = await getRepairTimeline(id);
      if (opts?.silent && isTabActive()) {
        // RA-UNREAD-SYNC — messages that arrive in the selected thread while the landlord is
        // looking at the page are read as they land: advance the divider snapshot past them
        // (no flash). Only when the divider isn't currently showing older unread messages.
        const key = selectedThreadKeyRef.current;
        const known = new Set(timelineRef.current.map((i) => i.id));
        const incoming = (item: TimelineItem) =>
          eventThreadKey(item) === key && isUnreadCandidate(item, key);
        const fresh = data.items.filter((i) => !known.has(i.id) && incoming(i));
        if (fresh.length > 0) {
          const newest = fresh.reduce((m, i) => (i.createdAt > m ? i.createdAt : m), fresh[0]!.createdAt);
          setDivider((d) =>
            d && d.key === key && !timelineRef.current.some((i) => incoming(i) && i.createdAt > d.after)
              ? { key, after: newest }
              : d,
          );
        }
      }
      setTimeline(data.items);
    } catch {
      // timeline failure is non-fatal
    } finally {
      if (!opts?.silent) setTimelineLoading(false);
    }
  }, [id]);

  const loadThreadReads = useCallback(async () => {
    if (!id || !hasAgenticSupport) return;
    try {
      const data = await getThreadReads(id);
      // Ignore refetched counts for threads with a mark-read in flight, so a stale non-zero
      // count cannot overwrite the optimistic zero before the POST lands.
      setThreadReads(
        data.threads.map((t) => (markInflightRef.current.has(t.threadKey) ? { ...t, unreadCount: 0 } : t)),
      );
    } catch {
      // unread state is a convenience — never blocks the page
    }
  }, [id, hasAgenticSupport]);

  const loadParties = useCallback(async () => {
    if (!id) return;
    setPartiesError(null);
    try {
      const data = await getRepairParties(id);
      setParties(data.parties);
    } catch (err) {
      setPartiesError(err instanceof Error ? err.message : 'Failed to load parties for this case');
    }
  }, [id]);

  useEffect(() => {
    loadRepair();
    loadTimeline();
    loadParties();
  }, [loadRepair, loadTimeline, loadParties]);

  useEffect(() => {
    void loadThreadReads();
  }, [loadThreadReads]);

  // RA-LIVE-REFRESH — a case-activity event (or reconnect / tab return) refreshes everything in
  // place. Never touches selectedThreadKey or the composer draft.
  useCaseActivityRefresh({
    caseId: id,
    onChange: () => {
      void loadRepair({ silent: true });
      void loadTimeline({ silent: true });
      void loadParties();
      bumpEscalationRefresh();
      void loadThreadReads();
      void refetchAttachments();
    },
  });

  // Track whether the landlord can actually see the page (visible AND focused).
  useEffect(() => {
    const update = () => setTabActive(isTabActive());
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
    };
  }, []);

  // Capture the divider snapshot when a thread becomes selected — BEFORE mark-read below.
  useEffect(() => {
    if (!threadReads || divider?.key === selectedThreadKey) return;
    // RA-UNREAD-MARK — a mark-read for this thread is still in flight (the landlord left and came
    // straight back): wait for its response to land in threadReads (which re-runs this effect) so
    // the snapshot is the fresh cursor, not the stale pre-read one.
    if (markInflightRef.current.has(selectedThreadKey)) return;
    const read = threadReads.find((t) => t.threadKey === selectedThreadKey);
    setDivider({ key: selectedThreadKey, after: read?.lastReadAt ?? CASE_THREAD_UNREAD_EPOCH });
  }, [threadReads, selectedThreadKey, divider]);

  // RA-UNREAD-MARK — mark the selected thread read when it is selected (or the tab regains
  // visibility/focus) and has unread messages. Optimistic zero keeps this from looping; the
  // guard on unreadCount > 0 makes it idempotent.
  useEffect(() => {
    if (!id || !threadReads || !tabActive || divider?.key !== selectedThreadKey) return;
    const unread = threadReads.find((t) => t.threadKey === selectedThreadKey)?.unreadCount ?? 0;
    if (unread <= 0 || markInflightRef.current.has(selectedThreadKey)) return;
    const key = selectedThreadKey;
    markInflightRef.current.add(key);
    setThreadReads((prev) => prev?.map((t) => (t.threadKey === key ? { ...t, unreadCount: 0 } : t)) ?? prev);
    markThreadRead(id, key)
      // RA-UNREAD-MARK — adopt the server's cursor so re-selecting this thread later never
      // re-shows a "New" divider from the stale pre-read lastReadAt (no wait on the refetch).
      .then((res) =>
        setThreadReads((prev) =>
          prev?.map((t) => (t.threadKey === key ? { ...t, lastReadAt: res.lastReadAt } : t)) ?? prev,
        ),
      )
      .catch(() => void loadThreadReads())
      .finally(() => markInflightRef.current.delete(key));
  }, [id, threadReads, tabActive, divider, selectedThreadKey, loadThreadReads]);

  async function handleClose() {
    if (!id) return;
    setClosing(true);
    setCloseError(null);
    try {
      const data = await closeRepair(id, { reason: closeReason.trim() || undefined });
      setRepair(data);
      setCloseDialogOpen(false);
      setCloseReason('');
      // Reload timeline to show the close activity
      await loadTimeline();
      // Closing tears down any open escalation server-side (BR-CASE-CLOSE-CLEANUP) —
      // tell the already-mounted CaseEscalationSection to reload so its card/banner
      // reflect that without a manual page reload.
      bumpEscalationRefresh();
    } catch (err) {
      setCloseError(err instanceof Error ? err.message : 'Failed to close repair');
    } finally {
      setClosing(false);
    }
  }

  const lastMessagePreview = (threadKey: string): string | null => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item = timeline[i]!;
      if (MESSAGE_EVENT_TYPES.has(item.eventType) && eventThreadKey(item) === threadKey) {
        const text = item.payload?.content;
        // Strip the agent's light markdown so the one-line preview doesn't show asterisks.
        return typeof text === 'string' ? text.replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim() : null;
      }
    }
    return null;
  };

  const threadOptions: ThreadOption[] = [
    {
      key: 'agent',
      label: 'Agent notes',
      disabled: false,
      kind: 'agent',
      name: 'Agent notes',
      roleLine: 'Private · you & the agent',
      title: 'Agent notes',
      subtitle: 'Private — only you and the agent see this',
      preview: lastMessagePreview('agent'),
    },
    ...(parties ?? []).map((p): ThreadOption => {
      const kindLabel = p.partyType === 'tenant' ? 'Tenant' : 'Vendor';
      const key = partyOptionValue(p);
      return {
        key,
        label: partyOptionLabel(p),
        disabled: !p.contactable,
        kind: p.partyType === 'tenant' ? 'tenant' : 'vendor',
        name: p.displayName,
        roleLine:
          !p.contactable && p.notContactableReason
            ? `${kindLabel} · ${p.notContactableReason}`
            : `${kindLabel} · sees this thread`,
        title: `${p.displayName} — ${kindLabel}`,
        subtitle: `The ${kindLabel.toLowerCase()} sees this thread; the agent may reply on your behalf`,
        preview: lastMessagePreview(key),
      };
    }),
  ];
  const activeThread = threadOptions.find((t) => t.key === selectedThreadKey) ?? threadOptions[0]!;

  const threadTabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const moveThreadFocusTo = useCallback((key: string) => {
    setSelectedThreadKey(key);
    threadTabRefs.current[key]?.focus();
  }, []);

  // WAI-ARIA APG tabs pattern (automatic activation, disabled tabs skipped) — mirrors
  // EmailInboxPage's bucket tablist, generalized to an arbitrary-length, dynamic thread list.
  const handleThreadTabListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = threadOptions.findIndex((t) => t.key === selectedThreadKey);
    if (currentIndex === -1 || threadOptions.length === 0) return;
    if (e.key === 'Home') {
      e.preventDefault();
      const first = threadOptions.find((t) => !t.disabled);
      if (first) moveThreadFocusTo(first.key);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      const last = [...threadOptions].reverse().find((t) => !t.disabled);
      if (last) moveThreadFocusTo(last.key);
      return;
    }
    let delta = 0;
    // Horizontal chips on mobile, vertical list on desktop — accept both axes.
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') delta = -1;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') delta = 1;
    else return;
    e.preventDefault();
    let idx = currentIndex;
    for (let i = 0; i < threadOptions.length; i++) {
      idx = (idx + delta + threadOptions.length) % threadOptions.length;
      const candidate = threadOptions[idx];
      if (candidate && !candidate.disabled) {
        moveThreadFocusTo(candidate.key);
        return;
      }
    }
  };

  const visibleTimeline = timeline.filter((item) => eventThreadKey(item) === selectedThreadKey);
  // RA-UNREAD-MARK — the selected thread of a visible tab is being (or has been) read: its
  // displayed count is never above 0 at any rendered frame, even before the mark effect runs.
  const unreadFor = (key: string) =>
    key === selectedThreadKey && tabActive ? 0 : (threadReads?.find((t) => t.threadKey === key)?.unreadCount ?? 0);
  // RA-UNREAD-DEF — the "New" divider sits before the first counterparty-authored message
  // newer than the snapshot.
  const dividerItemId =
    divider && divider.key === selectedThreadKey
      ? visibleTimeline.find(
          (i) =>
            isUnreadCandidate(i, selectedThreadKey) && i.createdAt > divider.after,
        )?.id
      : undefined;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4 space-y-3">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-4 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !repair) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] mt-6">
          <div className="text-center py-16 px-6 bg-card rounded-lg border border-border">
            <h2 className="text-xl font-semibold text-foreground mb-3">Error Loading Repair</h2>
            <p className="text-sm text-destructive mb-6">{error}</p>
            <div className="flex justify-center gap-3">
              <Button size="sm" onClick={() => void loadRepair()}>Retry</Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/repairs">Back to Repairs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!repair) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] mt-6">
          <div
            data-testid="repair-not-found"
            className="text-center py-16 px-6 bg-card rounded-lg border border-border"
          >
            <h2 className="text-xl font-semibold text-foreground mb-3">Repair Not Found</h2>
            <p className="text-sm text-muted-foreground mb-6">
              This repair request could not be found or you do not have access to it.
            </p>
            <div className="flex justify-center">
              <Button variant="outline" size="sm" asChild>
                <Link to="/repairs">Back to Repairs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const unitLabel = repair.unit
    ? formatUnitAddress(repair.unit.address, repair.unit.unitNumber, ' — ')
    : null;

  const chipClass = 'rounded-full px-2.5 font-bold';
  const mutedChipClass = `${chipClass} bg-muted text-foreground hover:bg-accent capitalize`;

  return (
    <PropertyTimezoneProvider timezone={propertyTimezone}>
    <div className="max-w-6xl mx-auto pb-6" data-testid="repair-detail-page">
      {/* Top bar: back link + page-level actions. The title lives in the summary card below
          (design mockup), not up here. */}
      <div className="px-[var(--page-padding-x)]">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <Link
            to="/repairs"
            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Repairs</span>
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            {!isProduction && (
              <span className="hidden sm:inline">
                <Link
                  to={`/repairs/${id}/test-harness`}
                  data-testid="test-harness-link"
                  className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
                >
                  Test Harness
                </Link>
              </span>
            )}
            <ImproveAgentButton
              caseId={repair.id}
              request={{
                start: { entry: 'case', anchor: { kind: 'none', caseId: repair.id }, caseId: repair.id },
                agentKind: 'repair',
                subtitle: unitLabel ? `About ${repair.title} · ${unitLabel}` : `About ${repair.title}`,
              }}
              className="min-h-[44px] font-semibold"
            />
            {!repair.closedAt ? (
              <Button
                ref={closeDialogTriggerRef}
                data-testid="manual-close-button"
                variant="outline"
                size="sm"
                className="min-h-[44px] font-semibold"
                onClick={() => setCloseDialogOpen(true)}
                aria-label="Mark repair as closed"
              >
                Mark as Closed
              </Button>
            ) : (
              <span className="text-sm text-muted-foreground">
                Closed on {formatTimestamp(asIsoDateTimeString(repair.closedAt), propertyTimezone, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div role="alert" className="px-[var(--page-padding-x)] mb-3">
          <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm">
            {error}
          </div>
        </div>
      )}

      {/* Escalation + paused-agent state (BR-AGENT-ESC-1, BR-AGENT-ESC-2) — banners ABOVE the
          summary card so "the agent needs you" / "the agent is stopped" is the first thing
          seen. Gated on agentic_support, same as the escalation queue page. Kept a separate,
          structurally distinct section from the policy controls (see CaseEscalationSection's
          own doc comment). */}
      {hasAgenticSupport && (
        <div className="px-[var(--page-padding-x)]">
          <CaseEscalationSection
            caseId={repair.id}
            testIdPrefix="repair"
            takeoverText="you messaged a party on this case directly"
            onChanged={() => { void loadRepair({ silent: true }); void loadTimeline(); }}
            refreshToken={escalationRefreshToken}
          />
        </div>
      )}

      {/* Summary card: title, status chips, inline agent policy switches (+ Pause), description
          and the unit/tenant/vendor/schedule footer — one surface, per the design mockup
          (RA-DETAIL-RESOLVE data preserved; Submitted-By/Created/Closed-At/Resolution-Notes
          deliberately not shown — audit-only fields). Test ids live on the same elements
          the old Details card used, so existing e2e specs reading them keep passing. */}
      <div className="px-[var(--page-padding-x)]">
        <section
          aria-label="Repair summary"
          className="grid gap-x-8 gap-y-3 rounded-xl border border-border bg-card p-4 md:grid-cols-[minmax(0,1fr)_auto] md:p-6"
        >
          <div className="flex min-w-0 flex-col gap-2 md:col-start-1 md:row-start-1">
            <h1 className="m-0 text-lg font-extrabold text-foreground md:text-[22px]">{repair.title}</h1>
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <RepairStatusBadge
                status={repair.status}
                vendorCompletedAt={repair.vendorCompletedAt}
              />
              {/* RA-AGENT-STATE — distinct from the status badge, never replacing it. */}
              <RepairAgentStateBadge agentState={repair.agentState} />
              {/* RA-CATEGORIZE-GATE: category/priority are nullable — a tenant-submitted
                  ticket may still be pending_triage with neither set. */}
              {repair.category && (
                <Badge variant="secondary" className={mutedChipClass} data-testid="repair-detail-category-badge">{repair.category}</Badge>
              )}
              {repair.priority && (
                <Badge variant="secondary" className={mutedChipClass} data-testid="repair-detail-priority-badge">{repair.priority}</Badge>
              )}
            </div>
          </div>

          {repair.description && (
            <p className="m-0 max-w-[780px] whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground md:col-start-1">
              {repair.description}
            </p>
          )}

          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-border pt-3 text-sm text-muted-foreground md:col-span-2">
            <span>
              <span className="mr-1.5 text-[11px] font-semibold uppercase tracking-wide">Unit</span>
              <span data-testid="repair-detail-unit" className="text-foreground">
                {repair.unit ? (
                  <>
                    {formatUnitAddress(repair.unit.address, repair.unit.unitNumber, ' — ')}
                  </>
                ) : (
                  '—'
                )}
              </span>
            </span>
            <span>
              <span className="mr-1.5 text-[11px] font-semibold uppercase tracking-wide">Tenant</span>
              <span data-testid="repair-detail-tenant" className="text-foreground">
                {repair.tenant ? (
                  <>
                    {repair.tenant.name}
                    {repair.tenant.contact ? ` — ${repair.tenant.contact}` : ''}
                  </>
                ) : (
                  '—'
                )}
              </span>
            </span>
            <span>
              <span className="mr-1.5 text-[11px] font-semibold uppercase tracking-wide">Vendor</span>
              <span data-testid="repair-detail-assigned-vendor" className="text-foreground">
                {repair.assignedVendor ? repair.assignedVendor.name : '—'}
              </span>
            </span>
            <span>
              <span className="mr-1.5 text-[11px] font-semibold uppercase tracking-wide">Scheduled</span>
              <span data-testid="repair-detail-scheduled-at" className="text-foreground">
                {repair.scheduledAt ? formatTimestamp(asIsoDateTimeString(repair.scheduledAt), propertyTimezone) : '—'}
              </span>
            </span>
          </div>

          {/* Agent policy: compact two-row stack beside the title at mid widths (the app's right
              rail leaves the content column narrower than the mockup canvas), one horizontal row
              only on very wide screens; below md it drops under the identity footer, as on the
              mobile board. */}
          {hasAgenticSupport && (
            <div className="md:col-start-2 md:row-span-2 md:row-start-1 md:w-[270px] 2xl:w-auto">
              <CaseAgentPolicySection
                caseId={repair.id}
                agentState={repair.agentState}
                onPaused={() => {
                  void loadRepair({ silent: true });
                  void loadTimeline();
                  bumpEscalationRefresh();
                }}
                // FM5-adjacent (iteration-12 web-ui review): the page's own agentState badge and
                // the Pause button's visibility are derived from resolved enablement, so an
                // enablement/review toggle here must refresh the page the same way onPaused
                // already does, or the header/button go stale until a full reload.
                onChanged={() => {
                  void loadRepair({ silent: true });
                  void loadTimeline();
                }}
              />
            </div>
          )}
        </section>
      </div>

      {/* Photos (RA-ATT-LANDLORD-READ) — omitted when the tenant attached none */}
      <RepairPhotoGallery
        attachments={attachmentsData?.attachments ?? []}
        caseCreatedAt={repair.createdAt}
        timezone={propertyTimezone}
        loadFailed={attachmentsFailed && !attachmentsData}
        onRetry={() => void refetchAttachments()}
      />

      {/* Conversation (RA-CONVERSATION-THREADS) — inbox-style: thread list on the left
          (chips on mobile), the selected thread on the right, and the composer docked at the
          bottom of the same card (RA-COMPOSE-AGENT, RA-COMPOSE-PARTY). Selecting a thread
          scopes both what is shown AND who the composer sends to. */}
      <div className="px-[var(--page-padding-x)] mt-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="repair-timeline">
          <h2 className="sr-only">Conversation</h2>
          <div className="flex flex-col md:flex-row">
            <div className="md:w-[260px] md:flex-none md:border-r md:border-border">
              <div className="hidden px-4 pb-2 pt-3.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground md:block">
                Conversations
              </div>
              <div
                role="tablist"
                aria-label="Conversation threads"
                className="flex gap-2 overflow-x-auto p-3 md:flex-col md:gap-0 md:overflow-visible md:p-0"
                onKeyDown={handleThreadTabListKeyDown}
              >
                {threadOptions.map((t) => {
                  const selected = selectedThreadKey === t.key;
                  const style = SENDER_STYLES[t.kind] ?? DEFAULT_SENDER_STYLE;
                  return (
                    <button
                      key={t.key}
                      ref={(el) => { threadTabRefs.current[t.key] = el; }}
                      type="button"
                      role="tab"
                      id={`repair-thread-tab-${t.key}`}
                      aria-selected={selected}
                      aria-controls="repair-thread-tabpanel"
                      tabIndex={selected ? 0 : -1}
                      onClick={() => setSelectedThreadKey(t.key)}
                      disabled={t.disabled}
                      data-testid={`repair-thread-${t.key}`}
                      className={`flex min-h-[44px] flex-none items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-left text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 md:w-full md:items-start md:gap-2.5 md:rounded-none md:border-l-[3px] md:px-4 md:py-3 md:text-[13px] ${
                        selected
                          ? 'bg-foreground text-background md:border-foreground md:bg-muted/60 md:text-foreground'
                          : 'bg-muted text-muted-foreground md:border-transparent md:bg-transparent md:text-foreground md:hover:bg-muted/40'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 flex-none items-center justify-center rounded-full text-[9px] font-extrabold md:h-[30px] md:w-[30px] md:text-xs ${style.avatar}`}
                      >
                        {style.letter}
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          {t.name}
                          <ThreadUnreadBadge count={unreadFor(t.key)} testId={`repair-thread-unread-${t.key}`} />
                        </span>
                        <span className="mt-px hidden text-[11px] font-normal text-muted-foreground md:block">
                          {t.roleLine}
                        </span>
                        {t.preview && (
                          <span className="mt-1 hidden max-w-[190px] truncate text-xs font-normal text-muted-foreground md:block">
                            {t.preview}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col border-t border-border md:border-t-0">
              <div className="border-b border-border px-4 py-3 md:px-5 md:py-4">
                <div className="text-[13px] font-bold text-foreground md:text-[15px]">{activeThread.title}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground md:text-xs">{activeThread.subtitle}</div>
              </div>

              {partiesError && (
                <p role="alert" className="m-0 px-4 pt-3 text-xs text-destructive md:px-5">{partiesError}</p>
              )}

              <div
                role="tabpanel"
                id="repair-thread-tabpanel"
                aria-labelledby={`repair-thread-tab-${selectedThreadKey}`}
                tabIndex={0}
                className="min-h-[200px] flex-1 p-3.5 md:p-5"
              >
                {timelineLoading ? (
                  <div role="status" aria-label="Loading conversation" className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full rounded-lg" />
                    ))}
                  </div>
                ) : timeline.length === 0 ? (
                  <p className="m-0 text-sm text-muted-foreground">No timeline events yet.</p>
                ) : visibleTimeline.length === 0 ? (
                  <p className="m-0 text-sm text-muted-foreground">No messages in this thread yet.</p>
                ) : (
                  <div className="flex flex-col gap-3 md:gap-4">
                    {visibleTimeline.map((item) => (
                      <Fragment key={item.id}>
                        {item.id === dividerItemId && (
                          <div
                            role="separator"
                            aria-label="New messages"
                            data-testid="repair-thread-new-divider"
                            className="flex items-center gap-2 text-xs font-bold text-primary"
                          >
                            <span className="h-px flex-1 bg-primary/30" />
                            New
                            <span className="h-px flex-1 bg-primary/30" />
                          </div>
                        )}
                        {MESSAGE_EVENT_TYPES.has(item.eventType) ? (
                          <MessageBubble
                            item={item}
                            feedback={{
                              caseId: repair.id,
                              reportsByEvent,
                              unitLabel,
                            }}
                          />
                        ) : (
                          <ActivityRow item={item} />
                        )}
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>

              {/* Message Compose (RA-COMPOSE-AGENT, RA-COMPOSE-PARTY) */}
              {!repair.closedAt && (
                <RepairComposer
                  repairId={repair.id}
                  selectedThreadKey={selectedThreadKey}
                  parties={parties}
                  partiesError={partiesError}
                  escalationOpen={repair.agentState === 'escalation_pending'}
                  onSent={() => {
                    void loadRepair({ silent: true });
                    void loadTimeline();
                    // A party send can raise a 'landlord_takeover' suspension server-side
                    // (RA-SUSPEND-BANNER) — tell the already-mounted CaseEscalationSection to
                    // reload so the paused banner appears immediately, without a page reload.
                    bumpEscalationRefresh();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Agent feedback: the landlord's own reports (RA-FB-REPORTED) — hidden when none. */}
      <div className="px-[var(--page-padding-x)] [&:not(:empty)]:mt-4">
        <AgentFeedbackReportsList caseId={repair.id} timezone={propertyTimezone} />
      </div>

      {/* Manual Close Dialog — rendered via portal so main[inert] doesn't trap it */}
      {closeDialogOpen && createPortal(
        <div
          ref={closeDialogRef}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="close-dialog-title"
          data-testid="close-ticket-modal"
        >
          <div className="bg-card rounded-lg border border-border p-6 max-w-sm w-full">
            <h3 id="close-dialog-title" className="text-lg font-semibold text-foreground m-0 mb-2">
              Mark as Closed?
            </h3>
            <p className="text-sm text-muted-foreground m-0 mb-4">
              This will close the repair ticket. The agent will no longer be notified of updates.
            </p>
            <div className="mb-4">
              <label
                htmlFor="close-reason-input"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Reason (optional)
              </label>
              <textarea
                id="close-reason-input"
                data-testid="close-reason-input"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="Describe why you're closing this ticket..."
                className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[80px] resize-y"
                aria-label="Close reason"
              />
            </div>
            {closeError && (
              <p role="alert" className="text-sm text-destructive mb-3">
                {closeError}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCloseDialogOpen(false);
                  setCloseReason('');
                  setCloseError(null);
                  closeDialogTriggerRef.current?.focus();
                }}
                disabled={closing}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-close-button"
                size="sm"
                onClick={handleClose}
                disabled={closing}
              >
                {closing ? 'Closing...' : 'Close Ticket'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
    </PropertyTimezoneProvider>
  );
}

export default RepairDetailPage;
